import {
  computeQuoteTextLayout, type MeasureWord, type QuoteTextLayout,
} from '@/lib/quoteCarousel/quoteTextLayout';
import {
  defaultQuoteTypography, type QuoteSlideProps, type QuoteSlideTypography,
} from '@/lib/quoteCarousel/buildQuoteSlideProps';

/** CSS `font-family` for a slide font: quoted (a catalog name may contain
 *  spaces or digits), with the sans-serif fallback. */
export function quoteFontFamilyCss(family: string): string {
  return `"${family.replace(/["\\]/g, '')}", sans-serif`;
}

let sharedCanvas: HTMLCanvasElement | null = null;
function getMeasureContext(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!sharedCanvas) sharedCanvas = document.createElement('canvas');
  return sharedCanvas.getContext('2d');
}

/** Per-character estimate used when no 2D context exists (jsdom). */
const FALLBACK_CHAR_WIDTH = 0.55;

/**
 * Word measurer in the slide's REAL font (family, weight, style): the canvas
 * `measureText` of the loaded FontFace. Before the font loads the canvas
 * falls back like the page does, and the slide re-measures once
 * `useLayerFont` reports it ready.
 */
export function createSlideMeasureWord(
  family: string,
  typography: Pick<QuoteSlideTypography, 'bold' | 'italic'>,
  ctx: CanvasRenderingContext2D | null = getMeasureContext(),
): MeasureWord {
  const cache = new Map<string, number>();
  const familyCss = quoteFontFamilyCss(family);
  const prefix = `${typography.italic ? 'italic ' : ''}${typography.bold ? 700 : 400}`;
  return (word, fontSize) => {
    const key = `${fontSize}|${word}`;
    const hit = cache.get(key);
    if (hit !== undefined) return hit;
    let width: number;
    if (ctx) {
      ctx.font = `${prefix} ${fontSize}px ${familyCss}`;
      width = ctx.measureText(word).width;
    } else {
      width = word.length * (FALLBACK_CHAR_WIDTH * fontSize);
    }
    cache.set(key, width);
    return width;
  };
}

/** The text geometry `QuoteSlide` draws for these props — also used by the
 *  editor's drag overlay to hit-test the text block. */
export function computeQuoteSlideLayout(props: QuoteSlideProps): QuoteTextLayout & { typography: QuoteSlideTypography } {
  const typography = props.typography ?? defaultQuoteTypography(props.style);
  const layout = computeQuoteTextLayout({
    style: props.style,
    width: props.width,
    height: props.height,
    text: props.text,
    sizeScale: typography.sizeScale,
    outlinePct: typography.outline?.widthPct ?? 0,
    textPosition: props.textPosition ?? null,
    measureWord: createSlideMeasureWord(props.brand.fontFamily, typography),
  });
  return { ...layout, typography };
}
